/**
 * Contact Section Component for Company Information and Engagement
 * 
 * A comprehensive contact and company information section featuring interactive
 * cards, social media integration, and rich content display. Encourages customer
 * engagement through multiple touchpoints and brand storytelling.
 * 
 * Content Areas:
 * - Company history card with engaging storytelling
 * - Gallery preview card showcasing craftsmanship
 * - Social media integration for community engagement
 * - Contact information and location details
 * 
 * Interactive Features:
 * - Hover animations on interactive cards
 * - Smooth transitions and visual feedback
 * - Link navigation to detailed pages
 * - Social media integration with external links
 * - Responsive card layouts for all screen sizes
 * 
 * Content Management:
 * - Sanity CMS integration for dynamic content
 * - Portable Text rendering for rich descriptions
 * - Fallback content for missing CMS data
 * - Dynamic image handling with optimization
 * - Flexible content structure adaptation
 * 
 * Visual Design:
 * - Professional card-based layout
 * - Consistent spacing and typography
 * - Brand-compliant color schemes
 * - Responsive grid system
 * - Engaging visual hierarchy
 * 
 * User Experience:
 * - Clear call-to-action elements
 * - Intuitive navigation flows
 * - Mobile-optimized touch interactions
 * - Accessible link descriptions
 * - Smooth page transitions
 * 
 * Social Integration:
 * - Instagram and Facebook links
 * - Social media contact component
 * - Community engagement encouragement
 * - Brand presence showcase
 * 
 * @param {object} data - Contact section data from Sanity CMS
 * @param {object} data.historyCard - Company history card content
 * @param {object} data.galleryCard - Gallery preview card content
 * @param {Array} data.socialMediaLinks - Social media contact links
 * 
 * @description Comprehensive contact and engagement section with interactive cards and social integration
 */

import Link from 'next/link';
import Image from 'next/image';
import { MapPin, Instagram, Facebook, History, Camera, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from '../../src/hooks/useTranslation';
import { } from '../../src/app/studio/lib/sanity';
import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';
import { PortableText } from '@portabletext/react';
import SocialMediaContact from './SocialMediaContact';

/**
 * Custom components for Portable Text rendering
 * Provides consistent typography and branding
 */
const portableTextComponents = {
  marks: {
    neuropol: ({children}) => <span className="font-neuropol">{children}</span>,
    montserrat: ({children}) => <span className="font-montserrat">{children}</span>,
    strong: ({children}) => <strong>{children}</strong>,
    em: ({children}) => <em>{children}</em>,
  },
  block: {
    normal: ({children}) => <p className="mb-4 last:mb-0">{children}</p>,
  },
};

const ContactSection = ({ data }) => {
  const { t } = useTranslation();
  // Animation state for cards
  const [hoveredCard, setHoveredCard] = useState(null);

  // Helper function to render portable text or fallback to string
  const renderPortableText = (portableText, fallbackText) => {
    // Check if portableText exists, is an array, has content, and that content has text
    if (portableText && 
        Array.isArray(portableText) && 
        portableText.length > 0 && 
        portableText.some(block => 
          block.children && 
          block.children.length > 0 && 
          block.children.some(child => child.text && child.text.trim().length > 0)
        )
    ) {
      return <PortableText value={portableText} components={portableTextComponents} />;
    }
    // Return fallback text if any of the above conditions aren't met
    return <p>{fallbackText}</p>;
  };

  // Get card data from Sanity or use defaults
  const historyCard = {
    id: 'history',
    title: data?.historyCard?.title || 'Our History',
    // Store only the reference to description, not the object itself
    descriptionData: data?.historyCard?.description,
    fallbackDescription: 'Discover the journey and passion behind our wooden bikes crafted in the Alps.',
    icon: <History className="w-8 h-8 mb-4 text-accent-color" />,
    image: data?.historyCard?.image ? urlForImage(data.historyCard.image) : '/images/home-ourHistoryPreview-branchy.jpeg',
    link: data?.historyCard?.link || '/about'
  };

  const galleryCard = {
    id: 'gallery',
    title: data?.galleryCard?.title || 'Gallery',
    // Store only the reference to description, not the object itself
    descriptionData: data?.galleryCard?.description,
    fallbackDescription: 'Explore our stunning collection of handcrafted wooden bicycles and see the beauty of craftsmanship.',
    icon: <Camera className="w-8 h-8 mb-4 text-accent-color" />,
    image: data?.galleryCard?.image ? urlForImage(data.galleryCard.image) : '/images/home-galleryPreview-cows.jpg',
    link: data?.galleryCard?.link || '/gallery'
  };

  const exploreCards = [historyCard, galleryCard];

  // Social section data
  const socialSection = {
    title: data?.socialSection?.title || 'Follow Our Journey',
    descriptionData: data?.socialSection?.description,
    fallbackDescription: 'Stay updated with our latest designs, events, and behind-the-scenes moments.',
    facebookLink: data?.socialSection?.facebookLink || 'https://facebook.com/woodalps',
    instagramLink: data?.socialSection?.instagramLink || 'https://www.instagram.com/woodalps_wooden_bikes/'
  };

  // Contact info data
  const contactInfo = {
    title: data?.contactInfo?.title || 'Let\'s Talk About Your Dream Bike',
    descriptionData: data?.contactInfo?.description,
    fallbackDescription: 'Whether you have questions about our products, custom orders, or just want to say hello, we\'d love to hear from you.',
    buttonText: data?.contactInfo?.buttonText || 'Contact Us',
    buttonLink: data?.contactInfo?.buttonLink || '/contact'
  };

  return (
    <section className="section bg-wood-brown/20 py-24">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-neuropol">{data?.title || 'Explore Our World'}</h2>
          <div className="w-20 h-1 bg-accent-color mx-auto mb-6"></div>
          {data?.subtitle && <p className="text-lg text-gray-600">{data.subtitle}</p>}
        </div>

        {/* Explore Cards - History and Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {exploreCards.map((card) => (
            <div 
              key={card.id}
              className="relative overflow-hidden rounded-xl shadow-lg group h-[300px]"
              onMouseEnter={() => setHoveredCard(card.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Background Image with Overlay */}
              <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-110">
                <Image
                  src={card.image}
                  alt={card.title}
                  fill
                  loader={sanityLoader}
                  sizes="(max-width: 640px) 100vw, 50vw"
                  className="object-cover object-center transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
              </div>
              
              {/* Content */}
              <div className="relative h-full flex flex-col justify-end p-8 text-white">
                <div className={`transition-all duration-500 ${hoveredCard === card.id ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-90'}`}>
                  <h3 className="text-2xl font-bold mb-2 font-neuropol">{card.title}</h3>
                  <div className="text-white/90 mb-4 font-montserrat max-w-sm">
                    {renderPortableText(card.descriptionData, card.fallbackDescription)}
                  </div>
                  <Link 
                    href={card.link}
                    className="inline-flex items-center px-4 py-2 bg-accent-color text-white rounded-md hover:bg-accent-color/90 transition-all duration-300 font-montserrat"
                  >
                    {t('buttons.find_out')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Social Media and Contact */}
        <SocialMediaContact 
          containerClassName="bg-wood-brown/20 p-10 rounded-xl shadow-md border border-gray-300"
          socialSection={socialSection}
          contactInfo={contactInfo}
          renderPortableText={renderPortableText}
        />
      </div>
    </section>
  );
};

export default ContactSection; 